import React, { FC, useEffect, useState } from "react";
import styled from "styled-components";
import whiteLogo from "../../assets/whiteLogo.svg";
import greenLogo from "../../assets/greenLogo.svg";
import { Button } from "../Button";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import { useNavigate } from "react-router";
import { HASH_ROUTES, ROUTES } from "../../constants/routes";
import { LanguageSelect } from "../Select/LanguageSelect";
import { useTranslation } from "../../lang/TranslationContext";
import { lightTheme } from "../../styles/theme";
import { scrollToAnchor } from "../../utils/scroll";
import { openInNewTab } from "../../utils/openInNewTab";

type HeaderProps = {
  blackText?: boolean;
};

export const Header: FC<HeaderProps> = ({ blackText = false }) => {
  const { translations } = useTranslation();
  const [isHeaderBlurred, setIsHeaderBlurred] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { height } = useWindowDimensions();

  const relocateAndScroll = (link: string, anchor: string) => {
    navigate(link);
    setTimeout(() => {
      scrollToAnchor(anchor);
    }, 100);
  };

  useEffect(() => {
    const onScroll = () => {
      if (window.pageYOffset > height - 130) {
        setIsHeaderBlurred(true);
      } else {
        setIsHeaderBlurred(false);
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [height]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflowY = "hidden";
    }
    if (!isMenuOpen) {
      document.body.style.overflowY = "auto";
    }
  }, [isMenuOpen]);

  return (
    <>
      <Wrap
        blur={isHeaderBlurred || isMenuOpen}
        blackText={blackText}
        isMenuOpen={isMenuOpen}
      >
        <InnerWrap>
          <Left>
            <LogoWrap onClick={() => setIsMenuOpen(false)}>
              <img src={blackText ? greenLogo : whiteLogo} alt="logo" />
            </LogoWrap>
            <DesktopLinks blackText={blackText}>
              <p
                onClick={() => relocateAndScroll(ROUTES.home, HASH_ROUTES.home)}
              >
                {translations.landing.header.home}
              </p>
              <p
                onClick={() =>
                  relocateAndScroll(ROUTES.home, HASH_ROUTES.features)
                }
              >
                {translations.landing.header.features}
              </p>
              <p
                onClick={() =>
                  relocateAndScroll(ROUTES.home, HASH_ROUTES.metrix)
                }
              >
                {translations.landing.header.metrix}
              </p>
              {/* <p
								onClick={() =>
									relocateAndScroll(ROUTES.home, HASH_ROUTES.download)
								}
							>
								{translations.landing.header.download}
							</p> */}
              <p
                onClick={() =>
                  relocateAndScroll(ROUTES.home, HASH_ROUTES.pricing)
                }
              >
                {translations.landing.header.pricing}
              </p>
            </DesktopLinks>
          </Left>
          <Right>
            <LanguageSelect smallUi />
            <Button
              onClick={() => openInNewTab("https://marketecho.app")}
              color="neutral"
              text={translations.landing.header.signUp}
            />
          </Right>
          <RightMobile>
            <BurgerButton
              blackText={blackText}
              onClick={() => setIsMenuOpen((open) => !open)}
              isOpen={isMenuOpen}
            >
              {isMenuOpen ? (
                <i className="pi pi-times" />
              ) : (
                <i className="pi pi-equals" />
              )}
            </BurgerButton>
          </RightMobile>
        </InnerWrap>
      </Wrap>
      <MobileWrap blackText={blackText} isMenuOpen={isMenuOpen}>
        <p
          onClick={() => {
            setIsMenuOpen(false);
            relocateAndScroll(ROUTES.home, HASH_ROUTES.home);
          }}
        >
          {translations.landing.header.home}
        </p>
        <p
          onClick={() => {
            setIsMenuOpen(false);
            relocateAndScroll(ROUTES.home, HASH_ROUTES.features);
          }}
        >
          {translations.landing.header.features}
        </p>
        <p
          onClick={() => {
            setIsMenuOpen(false);
            relocateAndScroll(ROUTES.home, HASH_ROUTES.metrix);
          }}
        >
          {translations.landing.header.metrix}
        </p>
        {/* <p
					onClick={() => {
						setIsMenuOpen(false);
						relocateAndScroll(ROUTES.home, HASH_ROUTES.download);
					}}
				>
					{translations.landing.header.download}
				</p> */}
        <p
          onClick={() => {
            setIsMenuOpen(false);
            relocateAndScroll(ROUTES.home, HASH_ROUTES.pricing);
          }}
        >
          {translations.landing.header.pricing}
        </p>
        <p
          onClick={() => {
            openInNewTab("https://marketecho.app");
            setIsMenuOpen(false);
          }}
        >
          <i className="pi pi-arrow-right" />
          {translations.landing.header.signUp}
        </p>
        <LanguageSelectWrap>
          <LanguageSelect optionsPosition="top" />
        </LanguageSelectWrap>
      </MobileWrap>
    </>
  );
};

const LanguageSelectWrap = styled.div`
  margin-top: 20px;
`;

const RightMobile = styled.div`
  display: none;
  @media screen and (max-width: 1000px) {
    display: flex;
    align-items: center;
  }
`;

const BurgerButton = styled.div<{ isOpen: boolean; blackText: boolean }>`
  display: flex;
  justify-content: flex-end;
  align-items: center;

  width: 50px;
  height: 60px;

  i {
    font-size: 25px;
    color: ${({ blackText }) =>
      blackText ? lightTheme.typography.primary : "white"};
  }
`;

const DesktopLinks = styled.div<{ blackText: boolean }>`
  margin-left: 80px;
  display: flex;
  gap: 36px;

  p {
    color: ${({ blackText }) =>
      blackText ? lightTheme.typography.primary : "white"};
    font-weight: ${({ blackText }) => (blackText ? 400 : 600)};
    font-size: 16px;
    cursor: pointer;
    transition: 0.2s;

    &:hover {
      opacity: 0.75;
    }
  }

  @media screen and (max-width: 1000px) {
    display: none;
  }
`;

const LogoWrap = styled.div`
  width: 135px;
  cursor: pointer;

  img {
    width: 100%;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
`;

const Wrap = styled.div<{
  blur: boolean;
  isMenuOpen: boolean;
  blackText: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 64px;
  z-index: 1000;
  position: fixed;
  background: transparent;
  backdrop-filter: ${({ blur, isMenuOpen, blackText }) =>
    (isMenuOpen && "blur(0px)") ||
    (blur && "blur(14px)") ||
    (blackText && "blur(14px)")};
  background: ${({ blur, isMenuOpen }) =>
    (isMenuOpen && "transparent") || (blur && "#00000024")};
  width: 100%;
  padding: 0 24px;
  transition: 0.5s;

  @media screen and (max-width: 700px) {
    padding: 0 16px;
  }
`;

const InnerWrap = styled.div`
  width: 100%;
  max-width: 1440px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Right = styled.div`
  display: flex;
  gap: 12px;

  @media screen and (max-width: 1000px) {
    display: none;
  }
`;

const MobileWrap = styled.div<{ isMenuOpen: boolean; blackText: boolean }>`
  width: 100%;
  height: 100vh;
  position: fixed;
  padding-top: 100px;
  background: #00331a44;
  backdrop-filter: blur(20px);
  transition: 0.5s;

  z-index: 999;
  z-index: ${({ isMenuOpen }) => (isMenuOpen ? "999" : "-1")};

  opacity: ${({ isMenuOpen }) => (isMenuOpen ? "1" : "0")};
  visibility: ${({ isMenuOpen }) => (isMenuOpen ? "visible" : "hidden")};

  display: flex;
  flex-direction: column;

  gap: 20px;
  padding-left: 32px;

  > p {
    color: ${({ blackText }) =>
      blackText ? lightTheme.typography.primary : "white"};
    font-weight: 600;
    font-size: 30px;

    i {
      font-size: 24px;
      margin-right: 14px;
    }
  }
`;
